import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { GlobalConstants } from 'src/app/base/global.constants';
import {
  getServices,
  saveServices,
  deleteServices,
  setServices,
  getServicesOrder,
  setServicesOrder,
  updateServicesOrder,
  getFields,
  setFields,
  getFieldsForTable,
  setFieldsForTable,
  setTotRecs,
  getServicesBasedOnSearch,
  getParentFields,
  setParentFields,
  getServicesByAppId,
  setServicesByAppId
} from '../actions/services.actions';
import { setRoles } from '../actions/roles.actions';
import { servicesTable } from '../selectors/services.selector';

@Injectable()
export class ServiceEffects {
  private actions$ = inject(Actions);
  private baseService = inject(HttpBaseService);
  private store = inject(Store);

  getServices$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getServices),
        map((action) => {
          if (!action.apiConstants) {
            return;
          }
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.filter).subscribe(
            (result: any) => {
              this.store.dispatch(
                setServices({
                  servicesTable: result
                })
              );
              this.store.dispatch(
                setTotRecs({
                  totalServRecords: result.total
                })
              );
            }
          );
        })
      ),
    { dispatch: false }
  );

  getServicesByAppId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getServicesByAppId),
        map((action) => {
          if (!action.apiConstants) {
            return;
          }
          return this.baseService[action.apiConstants?.method](action.apiConstants).subscribe((result: any) => {
            this.store.dispatch(
              setServicesByAppId({
                servicesByAppId: result
              })
            );
          });
        })
      ),
    { dispatch: false }
  );

  srcText = null;
  getServicesBasedOnSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getServicesBasedOnSearch),
        map((action) => {
          const searchQuery = {
            start: 0,
            length: 100,
            search: action.searchFields
          };
          if (!action.searchApiConstants) {
            return;
          }
          return this.baseService[action.searchApiConstants?.method](
            action.searchApiConstants,
            JSON.stringify(searchQuery)
          ).subscribe((result: any) => {
            this.srcText = null;
            this.store.dispatch(
              setServices({
                servicesTable: result
              })
            );
            this.store.dispatch(
              setTotRecs({
                totalServRecords: result.total
              })
            );
          });
        })
      ),
    { dispatch: false }
  );

  saveServices$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveServices),
        map((action) =>
          this.baseService[action.saveapiConstants?.method](action.saveapiConstants, action.servicesData).subscribe()
        )
      ),
    { dispatch: false }
  );

  deleteServices$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteServices),
        map((action) =>
          this.baseService[action.deleteserviceApiConstants?.method](
            action.deleteserviceApiConstants,
            action.serviceIds
          ).subscribe()
        )
      ),
    { dispatch: false }
  );

  getServicesOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getServicesOrder),
        map((action) => {
          return combineLatest([
            this.baseService[action.apiConstants?.roles.method](
              action.apiConstants.roles,
              GlobalConstants.dataTableHeaders
            ),
            this.baseService[action.apiConstants?.services.method](
              action.apiConstants.services,
              GlobalConstants.dataTableHeaders
            ),
            this.baseService[action.apiConstants?.order.method](action.apiConstants.order)
          ]).subscribe((results: any) => {
            this.store.dispatch(
              setRoles({
                rolesTable: results[0]
              })
            );
            this.store.dispatch(
              setServices({
                servicesTable: results[1]
              })
            );
            this.store.dispatch(
              setTotRecs({
                totalServRecords: results[1].total
              })
            );
            if (results[2]) {
              this.store.dispatch(
                setServicesOrder({
                  servicesOrder: results[2]
                })
              );
            } else {
              this.store.dispatch(
                setServicesOrder({
                  servicesOrder: { orders: null }
                })
              );
            }
          });
        })
      ),
    { dispatch: false }
  );

  updateServicesOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateServicesOrder),
        map((action) =>
          this.baseService[action.saveAPiConstants?.method](
            action.saveAPiConstants,
            action.servicesOrderData
          ).subscribe()
        )
      ),
    { dispatch: false }
  );

  getFields = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getFields),
        map((action) =>
          this.baseService[action.serviceAPiConstants?.method](action.serviceAPiConstants, action.params).subscribe(
            (result: any) => {
              this.store.dispatch(setFields({ fields: result }));
            }
          )
        )
      ),
    { dispatch: false }
  );

  getParentFields = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getParentFields),
        map((action) =>
          this.baseService[action.parentAPiConstants?.method](action.parentAPiConstants, action.params).subscribe(
            (result: any) => {
              this.store.dispatch(setParentFields({ parentFields: result }));
            }
          )
        )
      ),
    { dispatch: false }
  );

  getFieldsForTable$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getFieldsForTable),
        map((action) =>
          this.baseService[action.fstAPiConstants?.method](action.fstAPiConstants, action.params).subscribe(
            (result: any) => {
              this.store.dispatch(setFieldsForTable({ tblFields: result }));
            }
          )
        )
      ),
    { dispatch: false }
  );
}
